/**
 * Scenario comparison: up to three scenarios side by side with a difference row against the first one.
 * Pure data; the calculator decides what a scenario is and what its metrics are.
 */
export const MAX_SCENARIOS = 3;

export interface ScenarioMetric {
  key: string;
  label: string;
  /** Numbers get a delta; text (like a payoff date) is shown as is. */
  kind: "number" | "text";
  /** Display of a value (money, months...). Defaults to String. */
  format?: (v: number | string) => string;
  /** For a delta: which direction is "better". Only used to word the delta, never to hide it. */
  better?: "lower" | "higher";
}

export interface Scenario { id: string; name: string; values: Record<string, number | string> }

export interface ComparisonRow {
  key: string; label: string;
  cells: string[];
  /** Difference of each later scenario from the first (empty string for the first and for text metrics). */
  deltas: string[];
}

/** Keeps the first MAX_SCENARIOS scenarios. */
export function limitScenarios<T>(list: T[]): T[] {
  return list.slice(0, MAX_SCENARIOS);
}

export function signed(n: number, fmt: (v: number) => string): string {
  if (n === 0) return fmt(0);
  return `${n > 0 ? "+" : "-"}${fmt(Math.abs(n))}`;
}

/** Builds the table rows: one per metric, with each scenario's value and its difference from scenario 1. */
export function compareScenarios(scenarios: Scenario[], metrics: ScenarioMetric[]): ComparisonRow[] {
  const list = limitScenarios(scenarios);
  return metrics.map((m) => {
    const fmt = (v: number | string) => (m.format ? m.format(v) : String(v));
    const base = list[0]?.values[m.key];
    return {
      key: m.key, label: m.label,
      cells: list.map((s) => (s.values[m.key] === undefined ? "" : fmt(s.values[m.key]))),
      deltas: list.map((s, i) => {
        const v = s.values[m.key];
        if (i === 0 || m.kind !== "number" || typeof v !== "number" || typeof base !== "number") return "";
        return signed(v - base, (x) => fmt(x));
      }),
    };
  });
}
